import Grid from '@mui/material/Grid2';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { downloadFile, urlToBase64 } from '@/utils';
import {
  FileTypes,
  FileUploadStatus,
  PointOfInterest,
  useRemoveAttachmentsFromPoiMutation,
  useUpdatePointOfInterestFileMutation,
  AllPointsOfInterestForInspectionDataLoaderDocument,
  ImageType,
  File,
} from '@/__generated__/graphql';
import { Attachment } from '../types';
import { MediaViewer } from '@/components/shared/MediaViewer';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { FileDisplay } from '@/components/Analysis/shared/MediaUploadsAndViewer/FileDisplay';
import { usePoiAttachmentCaption } from '@/hooks/usePoiAttachmentCaption';
import { ListFilesSkeleton } from '@/components/Analysis/shared/MediaUploadsAndViewer/ListFilesSkeleton';

type Props = {
  selectedPoiInfo: PointOfInterest;
  attachments: Attachment[];
  allAttachments?: Attachment[];
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  isUpdating?: boolean;
  location?: string;
};

export const AttachmentsViewer = ({
  attachments = [],
  allAttachments,
  setAttachments,
  selectedPoiInfo,
  setIsUpdating,
  isUpdating,
  location = '',
}: Props) => {
  const [updatePoiFile] = useUpdatePointOfInterestFileMutation();
  const { updatePoiAttachmentCaption } = usePoiAttachmentCaption();
  const [removeAttachmentsFromPoi] = useRemoveAttachmentsFromPoiMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [validAttachments, setValidAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setValidAttachments(attachments);
  }, [attachments]);

  // Get valid files from selectedPoiInfo
  const validFiles = useMemo(() => {
    if (!selectedPoiInfo.files) return [];
    return selectedPoiInfo.files.filter(
      (file) =>
        file.uploadStatus &&
        [FileUploadStatus.Success, FileUploadStatus.Inprogress].includes(file.uploadStatus)
    );
  }, [selectedPoiInfo.files]);

  // Process a single file to attachment
  const processFileToAttachment = useCallback(async (file: File) => {
    const { fileName, resourcePath, type, id, uploadStatus, caption, imageType, uuid } = file;

    if (!resourcePath) {
      console.warn(`Missing resourcePath for file: ${fileName}`);
      return null;
    }

    const poiAttachmentUrl = getCloudfrontUrl(resourcePath);
    const isImage =
      type.includes('image/') || [FileTypes.Jpeg, FileTypes.Png].includes(type as FileTypes);

    let base64;
    if (isImage && poiAttachmentUrl) {
      try {
        base64 = await urlToBase64(poiAttachmentUrl);
      } catch (error) {
        console.error(`Error loading image for ${fileName}:`, error);
      }
    }

    return {
      id: id || `attachment-${fileName}-${Date.now()}`, // Ensure we always have an id
      name: fileName,
      base64,
      type,
      isNew: false,
      caption: caption || undefined,
      url: poiAttachmentUrl,
      uploadStatus: uploadStatus || FileUploadStatus.Success,
      uuid,
      imageType: imageType || ImageType.Other,
    };
  }, []);

  // Fetch all images
  const fetchImageURLs = useCallback(async () => {
    if (isUpdating || validFiles.length === 0) return;

    setLoading(true);
    try {
      const fetchedImages = await Promise.all(
        validFiles.map((file) => processFileToAttachment(file as File))
      );
      const validFetchedImages = fetchedImages.filter(Boolean) as Attachment[];

      if (validFetchedImages.length > 0) {
        setAttachments(validFetchedImages);
      } else {
        setAttachments([]);
      }
    } catch (error) {
      console.error('Error processing files:', error);
    } finally {
      setLoading(false);
    }
  }, [validFiles, processFileToAttachment, setAttachments, isUpdating]);

  // Track the previous files fingerprint to detect changes
  const [prevFilesFingerprint, setPrevFilesFingerprint] = useState<string>('');

  useEffect(() => {
    if (validFiles?.length > 0) {
      const currentFingerprint = validFiles.map((file) => file.id).join(',');
      if (attachments.length === 0 || currentFingerprint !== prevFilesFingerprint) {
        fetchImageURLs();
        setPrevFilesFingerprint(currentFingerprint);
      }
    }
  }, [validFiles, attachments.length, fetchImageURLs, prevFilesFingerprint]);

  const onUploadComplete = (index: number, file: Attachment) => {
    if (file.isNew && file.resourcePath) {
      const poiAttachmentUrl = getCloudfrontUrl(file.resourcePath);
      setAttachments((previous) => {
        const newAttachments = [...previous];
        newAttachments[index] = {
          ...newAttachments[index],
          url: poiAttachmentUrl,
          type: file.type,
          isNew: false,
          uploadStatus: FileUploadStatus.Success,
        };
        return newAttachments;
      });
      if (setIsUpdating) setIsUpdating(false);
    }
  };

  const handleDownloadAttachment = (attachmentIndex: number) => {
    const currentAttachment = attachments[attachmentIndex];
    if (!currentAttachment.url) return;
    downloadFile(currentAttachment.url, currentAttachment.name);
  };

  const handleRemoveAttachment = (attachmentIndex: number) => {
    const currentAttachment = attachments[attachmentIndex];
    if (!currentAttachment.id) return;
    const attachmentsToRemove: string[] = [];
    attachmentsToRemove.push(currentAttachment.id);
    const otherAttachmentsToRemove = allAttachments?.find(
      (eachAttachment) =>
        eachAttachment.id !== currentAttachment.id &&
        eachAttachment.uuid === `${currentAttachment.uuid}-${ImageType.WhiteBgPdf}` &&
        eachAttachment.imageType === ImageType.WhiteBgPdf
    );
    if (otherAttachmentsToRemove?.id) attachmentsToRemove.push(otherAttachmentsToRemove.id);
    removeAttachmentsFromPoi({
      variables: {
        input: {
          pointOfInterestId: selectedPoiInfo.id,
          fileIds: attachmentsToRemove,
        },
      },
      refetchQueries: [AllPointsOfInterestForInspectionDataLoaderDocument],
    });

    setAttachments(attachments.filter((attachment) => attachment.id !== currentAttachment.id));
  };

  const handelPoiAttachmentUpload = async (fileId: string | undefined) => {
    if (!fileId) {
      console.warn('Attempted to upload attachment with undefined fileId');
      return;
    }
    try {
      await updatePoiFile({
        variables: {
          input: {
            fileId,
            pointOfInterestId: selectedPoiInfo.id,
            uploadStatus: FileUploadStatus.Success,
          },
        },
        onError: (error) => {
          showSnackBar(error.message, 'error');
        },
      });
    } catch (error) {
      console.error('Error uploading attachment:', error);
      showSnackBar('Failed to update attachment status', 'error');
    }
  };

  const handelPoiImageCaption = async (
    updatedCaption: string | undefined,
    fileId: string | undefined,
    uuid: string | undefined
  ) => {
    if (!fileId) {
      console.warn('Cannot update caption: fileId or uuid is missing.', { fileId, uuid });
      return;
    }
    try {
      await updatePoiAttachmentCaption(selectedPoiInfo.id, uuid, fileId, updatedCaption);
      setAttachments((prev) =>
        prev.map((attachment) =>
          attachment.id === fileId ? { ...attachment, caption: updatedCaption } : attachment
        )
      );
    } catch (error) {
      console.error('Error updating image caption:', error);
      showSnackBar('Failed to update image caption', 'error');
    }
  };

  if (loading) {
    return <ListFilesSkeleton location={location} />;
  }
  if (!loading && attachments.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" mt={6}>
        <Typography variant="body2" color="primary.300">
          No attachments yet
        </Typography>
      </Stack>
    );
  }

  return (
    <Grid container spacing={2} columns={3} id={`${location}-attachments`}>
      <MediaViewer
        mediaFiles={validAttachments?.filter(
          (file) =>
            file &&
            (file.type.includes('image/') ||
              [FileTypes.Jpeg, FileTypes.Png].includes(file.type as FileTypes))
        )}
        handleDownloadAttachment={handleDownloadAttachment}
        handleRemoveAttachment={handleRemoveAttachment}
      >
        {attachments
          ?.filter((file) => !!file)
          .map((file, index) => (
            <Grid key={file.id || file.name || `attachment-${index}`}>
              <FileDisplay
                referenceId={selectedPoiInfo.id}
                collectionName="poi"
                selectedAttachment={file}
                selectedAttachmentIndex={index}
                showRemoveIcon={false}
                enableUpload={true}
                onUploadComplete={onUploadComplete}
                uploadAttachmentHandler={() => handelPoiAttachmentUpload(file.id)}
                imageCaptureHandler={(caption) =>
                  handelPoiImageCaption(caption, file.id, file.uuid)
                }
                location={location}
                handleSBSMode={() => {}}
                handleRemoveAttachment={handleRemoveAttachment}
                handleDownloadAttachment={handleDownloadAttachment}
              />
            </Grid>
          ))}
      </MediaViewer>
    </Grid>
  );
};
